import axios from 'axios';

interface LyricMatch {
  id: number;
  name: string;
  trackName: string;
  artistName: string;
  albumName: string;
  duration: number;
  instrumental: boolean;
  plainLyrics: string;
  syncedLyrics: string | null;
  score?: number;
}

// Configure API endpoint based on environment
const API_URL = 'https://api.syncdit.com/lyrics/search';

// Cache for lyrics to avoid repeated API calls
const lyricsCache = new Map<string, LyricMatch>();

export const lyricsService = {
  /**
   * Search for lyrics using SyncdIt-Lyrics API through Cloudflare Worker
   */
  searchLyrics: async (query: string): Promise<LyricMatch[]> => {
    try {
      // Check cache first
      const cacheKey = `syncdit-lyrics-${query}`;
      if (lyricsCache.has(cacheKey)) {
        console.log('Returning cached lyrics for:', cacheKey);
        return lyricsCache.get(cacheKey)!;
      }

      console.log('Searching SyncdIt-Lyrics API through Cloudflare Worker:', query);
      
      const response = await axios.get(API_URL, {
        params: { q: query }
      });
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`SyncdIt-Lyrics API success: Found ${response.data.length} results`);
        lyricsCache.set(cacheKey, response.data);
        return response.data;
      }
      
      console.error('Invalid response from SyncdIt-Lyrics API:', response.data);
      return [];
    } catch (error) {
      console.error('Error searching lyrics:', error);
      return [];
    }
  },

  /**
   * Find the best matching lyrics by scoring based on artist, track, album, and whether it has synced lyrics
   */
  findBestMatch: (
    results: LyricMatch[], 
    artistName: string, 
    trackName: string, 
    albumName?: string, 
    duration?: number
  ): LyricMatch | null => {
    if (!results || results.length === 0) {
      return null;
    }

    // Normalize inputs for comparison
    const normalizeText = (text: string) => text.toLowerCase().trim();
    const normalizedArtist = normalizeText(artistName);
    const normalizedTrack = normalizeText(trackName);
    const normalizedAlbum = albumName ? normalizeText(albumName) : '';
    
    // Score each result
    const scoredResults = results.map(result => {
      let score = 0;
      
      // Artist name exact match (most important)
      if (normalizeText(result.artistName) === normalizedArtist) {
        score += 100;
      } else if (normalizedArtist.includes(normalizeText(result.artistName)) || 
                normalizeText(result.artistName).includes(normalizedArtist)) {
        score += 50; // Partial match
      }
      
      // Track name exact match
      if (normalizeText(result.trackName) === normalizedTrack) {
        score += 80;
      } else if (normalizedTrack.includes(normalizeText(result.trackName)) || 
                normalizeText(result.trackName).includes(normalizedTrack)) {
        score += 40; // Partial match
      }
      
      // Album name match (if available)
      if (albumName && result.albumName) {
        if (normalizeText(result.albumName) === normalizedAlbum) {
          score += 30;
        } else if (normalizedAlbum.includes(normalizeText(result.albumName)) || 
                  normalizeText(result.albumName).includes(normalizedAlbum)) {
          score += 15; // Partial match
        }
      }
      
      // Duration match (if available) - allow small difference (within 3 seconds)
      if (duration && result.duration) {
        const durationDiff = Math.abs(duration - result.duration);
        if (durationDiff < 3) {
          score += 20;
        } else if (durationDiff < 10) {
          score += 10;
        }
      }
      
      // Bonus for having synced lyrics
      if (result.syncedLyrics) {
        score += 50;
      }
      console.log(result.syncedLyrics);
      return {
        ...result,
        score
      };
    });
    
    // Sort by score (highest first)
    scoredResults.sort((a, b) => (b.score || 0) - (a.score || 0));
    
    // Return the highest scoring result
    return scoredResults[0] || null;
  },

  /**
   * Get the best matching lyrics for a track
   */
  getLyrics: async (
    artistName: string, 
    trackName: string, 
    albumName?: string, 
    duration?: number
  ): Promise<{
    lyrics: string;
    synced: boolean;
    syncedLyrics?: Record<string, string>;
  }> => {
    try {
      // Create search query
      const query = `${trackName} ${artistName}`;
      
      // Search for lyrics
      const results = await lyricsService.searchLyrics(query);
      
      if (!results || results.length === 0) {
        return {
          lyrics: `No lyrics found for "${trackName}" by ${artistName}`,
          synced: false
        };
      }
      
      // Find best match
      const bestMatch = lyricsService.findBestMatch(results, artistName, trackName, albumName, duration);
      
      if (!bestMatch) {
        return {
          lyrics: `No good match found for "${trackName}" by ${artistName}`,
          synced: false
        };
      }
      
      // Process synced lyrics if available
      let syncedLyricsMap: Record<string, string> | undefined;
      
      if (bestMatch.syncedLyrics) {
        syncedLyricsMap = {};
        
        // Parse the synced lyrics format [MM:SS.ms] Lyrics line
        const lines = bestMatch.syncedLyrics.split('\n');
        
        lines.forEach(line => {
          const match = line.match(/\[(\d{2}):(\d{2})\.(\d{2})\]\s*(.*)/);
          if (match) {
            const minutes = parseInt(match[1]);
            const seconds = parseInt(match[2]);
            // Convert to milliseconds
            const timeMs = (minutes * 60 + seconds) * 1000;
            const text = match[4];
            
            syncedLyricsMap![timeMs.toString()] = text;
          }
        });
      }
      
      return {
        lyrics: bestMatch.plainLyrics || "No lyrics content available",
        synced: !!bestMatch.syncedLyrics,
        syncedLyrics: syncedLyricsMap
      };
    } catch (error) {
      console.error('Error getting lyrics:', error);
      return {
        lyrics: "Error fetching lyrics. Please try again later.",
        synced: false
      };
    }
  }
}; 